import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Auto-capture GPS location and save to Firebase
 * Call this when any page loads
 */
export const captureLocation = async (user, pageName, additionalData = {}) => {
  try {
    console.log(`📍 Auto-capturing location for page: ${pageName}`);

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      console.warn('⚠️ Geolocation not supported by browser');
      return null;
    }

    // Get current position
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });

    const locationData = {
      userId: user.id,
      userName: user.name,
      userRole: user.role,
      pageName: pageName,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: serverTimestamp(),
      capturedAt: new Date().toISOString(),
      ...additionalData
    };

    console.log('📍 Location captured:', locationData);

    // Save to Firebase
    const locationsRef = collection(db, 'locationLogs');
    const docRef = await addDoc(locationsRef, locationData);

    console.log('✅ Location saved to Firebase:', docRef.id);

    return {
      id: docRef.id,
      ...locationData
    };

  } catch (error) {
    console.error('❌ Error capturing location:', error);
    
    // Log the error to Firebase anyway
    try {
      await addDoc(collection(db, 'locationLogs'), {
        userId: user.id,
        userName: user.name,
        userRole: user.role,
        pageName: pageName,
        error: error.message,
        errorCode: error.code,
        timestamp: serverTimestamp(),
        capturedAt: new Date().toISOString()
      });
    } catch (logError) {
      console.error('Failed to log error:', logError);
    }

    return null;
  }
};

/**
 * Open Google Maps in satellite view at specific coordinates
 */
export const openInGoogleMaps = (latitude, longitude, label = 'Location') => {
  // Google Maps URL with satellite view
  // The 't=k' parameter enables satellite view
  const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}&basemap=satellite`;
  
  console.log('🗺️ Opening Google Maps:', url);
  
  // Open in new tab
  window.open(url, '_blank');
};

/**
 * Format coordinates for display
 */
export const formatCoordinates = (latitude, longitude, accuracy = null) => {
  const lat = latitude.toFixed(6);
  const lng = longitude.toFixed(6);
  const acc = accuracy ? ` (±${accuracy.toFixed(0)}m)` : '';
  
  return `${lat}, ${lng}${acc}`;
};

/**
 * Get distance between two coordinates in kilometers
 */
export const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  return distance; // in kilometers
};
